import * as React from "react";
import { FieldValues, Path, useFormContext } from "react-hook-form";
import { useLanguage } from "../../Language";
import { isFieldRequiredByNotification } from "./AdditionalEmailGroup";
import { BorrowerNotificationType } from "@tlc/ls2pac/dto/BorrowerNotificationType";
import { PhoneField } from "./Phone";

export interface PrimaryPhoneProps<T extends FieldValues> {
  name: Path<T>;
  required?: boolean;
  hidden?: boolean;
  disabled?: boolean;
  notificationTypes: BorrowerNotificationType[][];
}

export const PrimaryPhone = <T extends FieldValues>({
  name,
  required = false,
  hidden = false,
  disabled = false,
  notificationTypes,
}: PrimaryPhoneProps<T>) => {
  const { translate } = useLanguage();
  const { trigger, getFieldState } = useFormContext<T>();
  const { isTouched } = getFieldState(name);

  const isPhoneRequiredByNotifications = isFieldRequiredByNotification(
    notificationTypes,
    BorrowerNotificationType.Phone
  );

  const phoneRequired = React.useMemo(
    () =>
      required
        ? translate("isRequired", {
            LABEL: translate("ci_phoneNumber"),
          })
        : isPhoneRequiredByNotifications
        ? translate("isRequiredWhenNoticeTypeIs", {
            LABEL: translate("ci_phoneNumber"),
            NOTICE_TYPE: translate("ci_notificationType_Phone"),
          })
        : null,
    [isPhoneRequiredByNotifications, required]
  );

  React.useEffect(() => {
    if (isTouched) {
      trigger(name);
    }
  }, [phoneRequired]);

  return (
    (!hidden || !!phoneRequired) && (
      <PhoneField<T>
        disabled={disabled}
        sx={{ flexGrow: 1 }}
        label={translate("ci_phoneNumber")}
        name={name}
        options={{ required: phoneRequired }}
      />
    )
  );
};
